import React, { useState } from 'react';
import { Task } from '../types/Project';

interface TaskFormProps {
  projectId: string;
  onSubmit: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  existingTasks: Task[];
  initialData?: Task;
}

export const TaskForm: React.FC<TaskFormProps> = ({ 
  projectId, 
  onSubmit, 
  onCancel, 
  existingTasks,
  initialData
}) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    startDate: initialData?.startDate ? initialData.startDate.toISOString().split('T')[0] : '',
    endDate: initialData?.endDate ? initialData.endDate.toISOString().split('T')[0] : '',
    status: initialData?.status || 'not-started' as const,
    progress: initialData?.progress || 0,
    assignee: initialData?.assignee || '',
    dependencies: initialData?.dependencies || [] as string[]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      projectId,
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate)
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'progress' ? parseInt(value) : value
    }));
  };

  const handleDependencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({
      ...prev,
      dependencies: selectedOptions
    }));
  };

  return (
    <div className="task-form-overlay">
      <div className="task-form">
        <h2>{initialData ? 'タスクを編集' : '新しいタスクを作成'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">タスク名 *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">説明</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDate">開始日 *</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="endDate">終了日 *</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">ステータス</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="not-started">未開始</option>
                <option value="in-progress">進行中</option>
                <option value="completed">完了</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="progress">進捗 (%)</label>
              <input
                type="number"
                id="progress"
                name="progress"
                value={formData.progress}
                onChange={handleChange}
                min="0"
                max="100"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="assignee">担当者</label>
            <input
              type="text"
              id="assignee"
              name="assignee"
              value={formData.assignee}
              onChange={handleChange}
              placeholder="担当者名を入力"
            />
          </div>

          {existingTasks.length > 0 && (
            <div className="form-group">
              <label htmlFor="dependencies">依存タスク</label>
              <select
                id="dependencies"
                name="dependencies"
                multiple
                value={formData.dependencies}
                onChange={handleDependencyChange}
                className="dependencies-select"
              >
                {existingTasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.name}
                  </option>
                ))}
              </select>
              <small className="form-help">
                Ctrlキーを押しながらクリックで複数選択
              </small>
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              キャンセル
            </button>
            <button type="submit" className="btn-submit">
              {initialData ? '更新' : '作成'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
